import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router-dom"

import type { Status } from "../types"

import { ArrowLeft, ChevronRight, Lock } from "@/components/icons"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { useSidebar } from "@/components/ui/use-sidebar"
import { useRoutes } from "@/contexts/use-routes"
import { cn } from "@/lib/utils"

import { CourseFeedbackButton } from "./course-feedback-button"
import { useGetTrainingModules } from "../hooks/use-get-training-modules"
import { useGetTrainingProgress } from "../hooks/use-get-training-progress"
import { useTrainingLinks } from "../hooks/use-training-links"

type SectionStatus = "done" | "current" | "pending" | "locked"

function getModuleStatus(args: {
  isLocked: boolean
  isAllFinished: boolean
  isCurrent: boolean
}): SectionStatus {
  if (args.isLocked) return "locked"
  // "current" wins over "done" so the active group stays visually highlighted
  // even when all its sections happen to be completed.
  if (args.isCurrent) return "current"
  if (args.isAllFinished) return "done"
  return "pending"
}

function getSectionStatus(args: {
  isLocked: boolean
  isCompleted: boolean
  isCurrentUrl: boolean
}): SectionStatus {
  if (args.isLocked) return "locked"
  // Same priority as getModuleStatus: the active row keeps the active-ring dot
  // even after the user has completed it.
  if (args.isCurrentUrl) return "current"
  if (args.isCompleted) return "done"
  return "pending"
}

function StatusDot({ status }: { status: SectionStatus }) {
  if (status === "locked") {
    return (
      <span className="border-muted-foreground/40 bg-background flex h-2.5 w-2.5 items-center justify-center rounded-full border" />
    )
  }
  if (status === "done") {
    return <span className="bg-primary h-2.5 w-2.5 rounded-full" />
  }
  if (status === "current") {
    return (
      <span className="bg-background border-primary relative flex h-2.5 w-2.5 items-center justify-center rounded-full border-2">
        <span className="bg-primary h-1 w-1 rounded-full" />
      </span>
    )
  }
  return (
    <span className="border-muted-foreground/40 bg-background h-2.5 w-2.5 rounded-full border" />
  )
}

function TrackProgressBar({ pct }: { pct: number }) {
  return (
    <div className="bg-muted/60 h-1 w-full overflow-hidden rounded-full">
      <div
        className="bg-primary-ink h-full rounded-full transition-[width]"
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  )
}

export function TrainingNavMain() {
  const { pathname } = useLocation()
  const { state } = useSidebar()
  const { trainingLinks } = useTrainingLinks()
  const { trainingTracks } = useRoutes()

  const trainingModulesQuery = useGetTrainingModules()
  const progressQuery = useGetTrainingProgress()

  const { t } = useTranslation(undefined, { keyPrefix: "training" })

  const moduleStatusById = useMemo(() => {
    const map = new Map<string, Status>()
    trainingModulesQuery.data?.modules.forEach((m) => {
      if (m.module_id) map.set(m.module_id, m.status)
    })
    return map
  }, [trainingModulesQuery.data])

  const currentTrack = useMemo(() => {
    for (const track of trainingTracks) {
      const trackModuleIds = new Set(track.module_ids)
      const hasCurrentPage = trainingLinks
        .filter((link) => trackModuleIds.has(link.id))
        .some((link) => link.sections.some((s) => s.url === pathname))
      if (hasCurrentPage) return track
    }
    return null
  }, [trainingTracks, trainingLinks, pathname])

  // Scope to the current track while inside a course; when no course is open
  // (e.g. the training landing page) list every track so the sidebar is
  // still useful to navigate from.
  const trackGroups = trainingTracks
    .filter((track) => !currentTrack || track.id === currentTrack.id)
    .map((track) => {
      const trackModuleIds = new Set(track.module_ids)
      const links = trainingLinks.filter((link) => trackModuleIds.has(link.id))
      const progress = progressQuery.data?.tracks.find((tp) => tp.trackId === track.id)
      return { track, links, progress }
    })

  return (
    <>
      <SidebarGroup className="h-full overflow-x-hidden overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
        <SidebarGroupLabel>{t("interface.general.courses")}</SidebarGroupLabel>
        <SidebarMenu>
          {trackGroups.map(({ track, links, progress }) => {
            const isLocked = progress?.locked ?? false
            const trackPct = progress?.userProgressPct ?? 0
            const isCurrentTrack = links.some((link) =>
              link.sections.some((s) => s.url === pathname)
            )

            return (
              <SidebarMenuItem key={track.id} className="mb-2">
                <div className="flex flex-col gap-1.5 px-2 pt-1 pb-2 group-data-[collapsible=icon]:hidden">
                  <div className="flex items-center gap-2">
                    {isLocked && <Lock className="text-muted-foreground h-3.5 w-3.5 shrink-0" />}
                    <span
                      className={cn(
                        "text-xs font-bold tracking-wider uppercase",
                        isCurrentTrack ? "text-primary" : "text-foreground",
                        isLocked && "opacity-50"
                      )}
                    >
                      {t(`tracks.${track.id}.title`, track.display_name)}
                    </span>
                    {state === "expanded" && !isLocked && (
                      <span className="text-muted-foreground ml-auto text-[10px] tabular-nums">
                        {Math.round(trackPct)}%
                      </span>
                    )}
                  </div>
                  {state === "expanded" && !isLocked && <TrackProgressBar pct={trackPct} />}
                </div>

                {state === "expanded" && (
                  <ul className="flex flex-col gap-0.5">
                    {links.map((trainingLink) => {
                      const isCurrentGroup = trainingLink.sections.some(
                        (link) => link.url === pathname
                      )
                      const isAllModulesFinished = trainingLink.sections.every(
                        (section) => moduleStatusById.get(section.id) === "completed"
                      )
                      const hasInProgressSection = trainingLink.sections.some(
                        (section) =>
                          moduleStatusById.get(section.id) !== "completed" &&
                          (isCurrentGroup || moduleStatusById.get(section.id) === "inProgress")
                      )

                      const moduleStatus = getModuleStatus({
                        isLocked,
                        isAllFinished: isAllModulesFinished,
                        isCurrent: isCurrentGroup || hasInProgressSection,
                      })

                      return (
                        <li key={trainingLink.title}>
                          {/* Remount on track change so uncontrolled defaultOpen re-evaluates */}
                          <Collapsible
                            key={currentTrack?.id ?? "none"}
                            defaultOpen={isCurrentGroup}
                          >
                            <CollapsibleTrigger
                              className={cn(
                                "group/collapsible hover:bg-sidebar-accent flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm",
                                isLocked && "pointer-events-none opacity-40"
                              )}
                            >
                              <StatusDot status={moduleStatus} />
                              <span
                                className={cn(
                                  "flex-1 text-left",
                                  isCurrentGroup && "text-primary font-semibold"
                                )}
                              >
                                {t(`links.${trainingLink.title}.title`)}
                              </span>
                              <ChevronRight className="text-muted-foreground h-3 w-3 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <ul className="relative ml-[15px] flex flex-col border-l py-1">
                                {trainingLink.sections.map((section) => {
                                  const isCurrentUrl = pathname === section.url
                                  const status = getSectionStatus({
                                    isLocked,
                                    isCompleted: moduleStatusById.get(section.id) === "completed",
                                    isCurrentUrl,
                                  })

                                  const rowClassName = cn(
                                    "hover:bg-sidebar-accent flex items-center gap-2 rounded-md py-1 pr-2 pl-3 text-sm",
                                    isCurrentUrl && "text-primary font-semibold",
                                    !isCurrentUrl && "text-muted-foreground",
                                    isLocked && "cursor-not-allowed opacity-50"
                                  )
                                  const rowContent = (
                                    <>
                                      <span className="absolute -left-[5px] flex h-full items-center">
                                        <span className="bg-sidebar p-0.5">
                                          <StatusDot status={status} />
                                        </span>
                                      </span>
                                      <span className="truncate">
                                        {t(`links.${trainingLink.title}.sections.${section.title}`)}
                                      </span>
                                    </>
                                  )

                                  return (
                                    <li key={section.id} className="relative">
                                      {isLocked ? (
                                        <span aria-disabled="true" className={rowClassName}>
                                          {rowContent}
                                        </span>
                                      ) : (
                                        <Link className={rowClassName} to={section.url}>
                                          {rowContent}
                                        </Link>
                                      )}
                                    </li>
                                  )
                                })}
                              </ul>
                            </CollapsibleContent>
                          </Collapsible>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroup>
      {currentTrack && (
        <SidebarGroup className="mt-auto">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip={t("interface.buttons.back_to_landing", "Back")}>
                <Link className="text-muted-foreground gap-2" to="/training">
                  <ArrowLeft className="h-4 w-4" />
                  {state === "expanded" && (
                    <span>{t("interface.buttons.back_to_landing", "Back")}</span>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <CourseFeedbackButton
              className={"w-full justify-start text-sm"}
              showText={state === "expanded"}
            />
          </SidebarMenu>
        </SidebarGroup>
      )}
    </>
  )
}
